<template>
  <ClientOnly>
    <div v-if="sideFilterMenu.isShown" class="filter-menu__backdrop" />
    <Transition name="show-menu">
      <aside
        v-if="sideFilterMenu.isShown"
        v-on-click-outside="onClose"
        class="filter-menu"
      >
        <div v-if="isPending" class="filter-menu__loader">
          <span class="ui-loader" />
        </div>
        <div class="filter-menu__header">
          <div class="filter-menu__header-wrapper">
            <img src="~/assets/icons/filter.svg" alt="filter" />
            <p>{{ _T("@Filters") }}</p>
            <span v-if="hasItems" class="filter-menu__header-count">
              {{ items.length }}
            </span>
          </div>
          <ButtonClose @click="onClose" />
        </div>
        <SelectedFilter />
        <FilterBar />
        <div class="filter-menu__footer-bar">
          <div class="filter-menu__footer-bar-actions">
            <ButtonSecondary
              type="button"
              aria-label="reset filters"
              @click="resetItems"
            >
              <span class="filter-menu__footer-action-caption">
                {{ _T("@Clear filters") }}
              </span>
            </ButtonSecondary>
            <ButtonPrimary
              type="button"
              :aria-label="_T('@Accept filters')"
              @click="onClose"
            >
              <span class="filter-menu__footer-action-caption">
                {{ _T("@Accept filters") }}
              </span>
            </ButtonPrimary>
          </div>
          <span class="filter-menu__footer-bar-count">
            {{ _T("@Show filter result") }} {{ productStore.totalItems }}
          </span>
        </div>
      </aside>
    </Transition>
  </ClientOnly>
</template>

<script setup>
import { useAppearanceStore } from "~/store/appearanceStore";
import { vOnClickOutside } from "@vueuse/components";
import FilterBar from "~/modules/productCatalog/ui/components/filter/bar/FilterBar.vue";
import SelectedFilter from "~/modules/productCatalog/ui/components/filter/selected/SelectedFilter.vue";
import { useProductStore } from "~/store/productStore";
import ButtonSecondary from "~/modules/shared/buttons/ButtonSecondary.vue";
import ButtonPrimary from "~/modules/shared/buttons/ButtonPrimary.vue";
import { useSelectedFilter } from "~/modules/productCatalog/composables/useSelectedFilter";
import ButtonClose from "~/modules/shared/buttons/ButtonClose.vue";

const props = defineProps({
  isPending: { type: Boolean, required: false },
});

const productStore = useProductStore();
const { sideFilterMenu } = useAppearanceStore();

const { items, hasItems, resetItems } = useSelectedFilter();

const onClose = () => {
  sideFilterMenu.isShown = false;
};

watch(
  () => sideFilterMenu.isShown,
  () => {
    if (sideFilterMenu.isShown) {
      document.documentElement.style.overflow = "hidden";
    } else {
      document.documentElement.style.overflow = "auto";
    }
  },
);
</script>

<style scoped lang="scss">
.filter-menu {
  position: fixed;
  top: 0;
  right: 0;
  z-index: 1000;

  height: 100dvh;
  width: 85%;

  @include flex-container(column, flex-start, flex-start);
  gap: 16px;

  background-color: white;
  border-top-left-radius: 16px;
  border-bottom-left-radius: 16px;

  @extend %horizontal-scroll;
  overflow: auto;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &__header {
    width: 100%;

    @include flex-container(row, space-between, center);
    gap: 8px;

    border-bottom: 1px solid var(--color-sky-light);

    padding: 8px 16px;
  }

  &__header-wrapper {
    @include flex-container(row, flex-start, center);
    gap: 4px;

    @include font(14, 20);
    color: var(--color-primary-base);
  }

  &__header-count {
    min-width: 16px;
    min-height: 16px;

    @include flex-container(row, center, center);

    @include font(14, 16);
    color: white;

    border-radius: 8px;
    background-color: var(--color-primary-base);

    padding-inline: 2px;
  }

  &__footer-bar {
    position: sticky;
    bottom: 0;
    left: 0;

    width: 100%;

    @include flex-container(column, flex-start, center);
    gap: 8px;

    background-color: white;
    border-top-right-radius: 16px;
    border-top-left-radius: 16px;

    box-shadow: 0 -2px 17px 0 rgba(0, 0, 0, 0.1215686275);

    padding: 8px;
  }

  &__footer-bar-actions {
    width: 100%;

    @include flex-container(row, center, center);
    gap: 8px;
  }

  &__footer-action-caption {
    @include font(14, 18);
  }

  &__footer-bar-count {
    @include font(14, 18);
    color: var(--color-sky-darker);
  }

  &__loader {
    width: 100%;
    height: 100dvh;

    @include flex-container(row, center, center);

    background: hsla(0, 0%, 100%, 0.8);

    z-index: 9999;
    position: fixed;
    inset: 0;
  }

  &__backdrop {
    position: fixed;

    left: 0;
    right: 0;
    top: 0;
    bottom: 0;

    z-index: 999;

    backdrop-filter: blur(4px) opacity(1);
    background-color: rgb(0, 0, 0, 0.5);
  }
}
</style>
